import { useCallback, useState, useRef, ChangeEvent } from 'react';
import { ArrowUpTrayIcon, XCircleIcon } from '@heroicons/react/24/outline';
import { Button } from '@/components/ui/Button';

interface FileUploadZoneProps {
    /** Accepted file extensions (e.g., ['.csv', '.zip']) */
    acceptedTypes: string[];
    /** Maximum file size in megabytes */
    maxSizeMB: number;
    /** Callback when a valid file is selected */
    onFileSelected: (file: File) => void;
    /** Disabled state */
    disabled?: boolean;
}

/**
 * Simple file upload button with validation.
 * Validates file type and size before calling onFileSelected.
 */
export function FileUploadZone({
    acceptedTypes,
    maxSizeMB,
    onFileSelected,
    disabled = false,
}: FileUploadZoneProps) {
    const [error, setError] = useState<string | null>(null);
    const inputRef = useRef<HTMLInputElement>(null);

    const maxSizeBytes = maxSizeMB * 1024 * 1024;

    const validateFile = useCallback(
        (file: File): string | null => {
            const fileName = file.name.toLowerCase();
            const hasValidExtension = acceptedTypes.some((ext) =>
                fileName.endsWith(ext.toLowerCase())
            );

            if (!hasValidExtension) {
                return `Invalid file type. Accepted: ${acceptedTypes.join(', ')}`;
            }

            if (file.size > maxSizeBytes) {
                return `File too large. Maximum size: ${maxSizeMB}MB`;
            }

            return null;
        },
        [acceptedTypes, maxSizeBytes, maxSizeMB]
    );

    const handleFile = useCallback(
        (file: File) => {
            const validationError = validateFile(file);
            if (validationError) {
                setError(validationError);
                return;
            }

            setError(null);
            onFileSelected(file);
        },
        [validateFile, onFileSelected]
    );

    const handleChange = useCallback(
        (e: ChangeEvent<HTMLInputElement>) => {
            const file = e.target.files?.[0];
            if (file) {
                handleFile(file);
            }
            // Reset input so same file can be selected again
            if (inputRef.current) {
                inputRef.current.value = '';
            }
        },
        [handleFile]
    );

    const handleClick = useCallback(() => {
        if (!disabled && inputRef.current) {
            inputRef.current.click();
        }
    }, [disabled]);

    const acceptString = acceptedTypes.join(',');

    return (
        <div>
            <input
                ref={inputRef}
                type="file"
                accept={acceptString}
                onChange={handleChange}
                className="hidden"
                disabled={disabled}
                data-testid="file-input"
            />

            <div className="flex items-center gap-3">
                <Button
                    variant="secondary"
                    onClick={handleClick}
                    disabled={disabled}
                >
                    <ArrowUpTrayIcon className="h-5 w-5 mr-2" />
                    Choose File
                </Button>
                <span className="text-sm text-gray-500 dark:text-gray-400">
                    {acceptedTypes.join(', ')} (max {maxSizeMB}MB)
                </span>
            </div>

            {error && (
                <div className="mt-2 flex items-center gap-2 text-red-600 dark:text-red-400">
                    <XCircleIcon className="h-4 w-4 shrink-0" />
                    <p className="text-sm">{error}</p>
                </div>
            )}
        </div>
    );
}
